import { RocketIcon } from '@/components/icons/RocketIcon'
import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useActiveBusiness } from '@/hooks/useCurrentActiveBusiness'
import { authClient } from '@/lib/auth/client'
import { orpc } from '@/lib/orpc/client'
import { BuildingIcon } from '@phosphor-icons/react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Calendar, FileText, PenTool, Settings, Zap } from 'lucide-react'

export const Route = createFileRoute('/_dashboard/')({
  component: DashboardPage,
})

function DashboardPage() {
  const { activeBusiness } = useActiveBusiness()
  const { data: session } = authClient.useSession()

  // Get articles data for the active business
  const { data: articles, isPending: articlesLoading } = useQuery(
    orpc.articles.getListOfArticles.queryOptions({
      input: {
        status: 'published',
      },
    }),
  )

  // If no active business, show welcome screen
  if (!activeBusiness) {
    return (
      <div className="font-sans grid grid-rows-[20px_1fr_20px] items-center justify-items-center p-8 pb-20 gap-16 sm:p-20">
        <main className="flex flex-col gap-[32px] row-start-2 items-center sm:items-start">
          <div className="mb-8 text-center">
            <h1 className="mb-4 text-3xl font-bold text-gray-800">
              Welcome to RocketRank
            </h1>
            <p className="mb-6 max-w-md text-gray-600">
              Ready to launch your business? Create your first business profile
              and start ranking.
            </p>
            <Link to="/create-business">
              <Button className="px-8 py-3 text-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg transition-all duration-200 transform hover:from-blue-700 hover:to-purple-700 hover:shadow-xl hover:scale-105">
                <RocketIcon className="mr-2 w-5 h-5" />
                Create Business
              </Button>
            </Link>
          </div>
        </main>
      </div>
    )
  }

  // Get recent articles (last 3)
  const recentArticles = articles?.slice(0, 4) || []

  return (
    <div className="p-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <PageHeader
          title={`Welcome back${session?.user.name ? `, ${session.user.name.split(' ')[0]}` : ''}!`}
          description="Manage your content and grow your business with AI-powered articles."
          className="mb-8"
        />

        {/* Stats Cards */}
        {/* <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-5">
          <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Articles
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {totalArticles}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Published</p>
                <p className="text-2xl font-bold text-gray-900">
                  {publishedArticles}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Edit3 className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Drafts</p>
                <p className="text-2xl font-bold text-gray-900">
                  {draftArticles}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Scheduled</p>
                <p className="text-2xl font-bold text-gray-900">
                  {scheduledArticles}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <PenTool className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Writing</p>
                <p className="text-2xl font-bold text-gray-900">
                  {writingArticles}
                </p>
              </div>
            </div>
          </div>
        </div> */}

        <div className="grid gap-8 lg:grid-cols-2">
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <Link to="/write">
                  <Button
                    variant="outline"
                    className="w-full h-20 flex flex-col items-center justify-center gap-2"
                  >
                    <PenTool className="w-6 h-6" />
                    <span>Create Article</span>
                  </Button>
                </Link>
                <Link to="/schedule">
                  <Button
                    variant="outline"
                    className="w-full h-20 flex flex-col items-center justify-center gap-2"
                  >
                    <Calendar className="w-6 h-6" />
                    <span>Schedule Content</span>
                  </Button>
                </Link>
                <Link to="/articles">
                  <Button
                    variant="outline"
                    className="w-full h-20 flex flex-col items-center justify-center gap-2"
                  >
                    <FileText className="w-6 h-6" />
                    <span>Your Articles</span>
                  </Button>
                </Link>
                <Link to="/settings/integrations">
                  <Button
                    variant="outline"
                    className="w-full h-20 flex flex-col items-center justify-center gap-2"
                  >
                    <Zap className="w-6 h-6" />
                    <span>Integrations</span>
                  </Button>
                </Link>
                <Link to="/settings/business">
                  <Button
                    variant="outline"
                    className="w-full h-20 flex flex-col items-center justify-center gap-2"
                  >
                    <BuildingIcon className="w-6 h-6" />
                    <span>Business Details</span>
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Recent Articles */}
          <Card>
            <CardHeader>
              <CardTitle className="flex justify-between items-center">
                <span>Recent Articles</span>
                <Link
                  className="text-sm leading-none font-medium hover:text-foreground text-muted-foreground"
                  to="/articles"
                >
                  View All
                </Link>
              </CardTitle>
            </CardHeader>

            <CardContent>
              {articlesLoading && (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  ))}
                </div>
              )}
              {!articlesLoading && recentArticles.length > 0 && (
                <div className="space-y-2">
                  {recentArticles.map((article) => (
                    <div
                      key={article.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {article.title || 'Untitled Article'}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {new Date(article.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="ml-2">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            article.status === 'published'
                              ? 'bg-green-100 text-green-800'
                              : article.status === 'draft'
                                ? 'bg-yellow-100 text-yellow-800'
                                : article.status === 'scheduled-for-publishing'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-purple-100 text-purple-800'
                          }`}
                        >
                          {article.status === 'published'
                            ? 'Published'
                            : article.status === 'draft'
                              ? 'Draft'
                              : article.status === 'scheduled-for-publishing'
                                ? 'Scheduled'
                                : 'Writing'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!articlesLoading && recentArticles.length === 0 && (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No articles yet</p>
                  <Link to="/write">
                    <Button size="sm" className="mt-2">
                      Create your first article
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Business Info */}
        <div className="mt-8 p-6 rounded-lg border border-border">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex justify-between">
                <h3 className="text-lg font-semibold">
                  {activeBusiness.businessName}
                </h3>
                <Link to="/business-info">
                  <Button variant="outline" size="sm">
                    <Settings className="w-4 h-4 mr-2" />
                    Edit Business
                  </Button>
                </Link>
              </div>
              <div className="flex flex-col gap-4 text-muted-foreground">
                <div>
                  <p className="font-medium text-xs">Business Description</p>
                  <p>{activeBusiness.businessDescription}</p>
                </div>
                <div>
                  <p className="font-medium text-xs">Industry</p>
                  <p>{activeBusiness.industry}</p>
                </div>
                <div>
                  <p className="font-medium text-xs">Target Audience</p>
                  <p>{activeBusiness.targetAudience}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
